import { IDanceClass, ISelectedClassTrial, ISelectedClassFee, DAY_OF_WEEK, FEE_TAX_SETTING, IDanceSchool, IOptionsItem, IDateOnlyRange } from "../models";
import { calculatePostTaxAmount } from "./currencyFormatter";
import { COUNTRY_CODE } from "../constants/intl";
import { DATE_FORMATS } from "../constants";

import dayjs, { Dayjs } from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const CLASSES_FREE_AFTER_COUNT = 4;

const getClassDate = (classDetails: IDanceClass): Dayjs => {
  const startOfWeek = dayjs().startOf('week');
  let daysToAdd = 0;
  switch (classDetails.dayOfWeek) {
    case DAY_OF_WEEK.MONDAY: daysToAdd = 1; break;
    case DAY_OF_WEEK.TUESDAY: daysToAdd = 2; break;
    case DAY_OF_WEEK.WEDNESDAY: daysToAdd = 3; break;
    case DAY_OF_WEEK.THURSDAY: daysToAdd = 4; break;
    case DAY_OF_WEEK.FRIDAY: daysToAdd = 5; break;
    case DAY_OF_WEEK.SATURDAY: daysToAdd = 6; break;
  }

  const classDayOfWeek = startOfWeek.add(daysToAdd, 'days');
  const classStartTime = dayjs(classDetails.startTime, 'h:mma');

  return classDayOfWeek.set('hour', classStartTime.hour()).set('minute', classStartTime.minute());
}

export const calculateAgeAtStartOfTerm = (dateOfBirth: Dayjs, danceSchool: IDanceSchool): number => {
  const firstTermStart = danceSchool.latestEnrolmentYearSettings.termDates[0].startDate;
  return Math.floor(firstTermStart.diff(dateOfBirth, 'years', true));
}

export const mapSelectedClassFees = (selectedClasses: IDanceClass[], countryCode: COUNTRY_CODE): ISelectedClassFee[] => {

  return selectedClasses
    .map(x => ({
      classDetails: x,
      fee: x.feeTaxSetting === FEE_TAX_SETTING.TAX_EXCLUDED ? calculatePostTaxAmount(x.fee, countryCode) : x.fee,
      classDate: getClassDate(x)
    }) as ISelectedClassFee)
    .sort((a, b) => b.fee - a.fee)
    .map((x, index) => ({
      ...x,
      noCharge: index > (CLASSES_FREE_AFTER_COUNT - 1)
    }))
    .sort((a, b) => a.classDate.isBefore(b.classDate) ? -1 : a.classDate.isAfter(b.classDate) ? 1 : 0);
}

export const getTermForDate = (date: Dayjs, danceSchool: IDanceSchool, exactMatch?: boolean): ({ termNumber: number; } & IDateOnlyRange) | null => {
  const matchingTerm = danceSchool.latestEnrolmentYearSettings.termDates
    .find(term => (!exactMatch && term.endDate.isSameOrAfter(date, 'date')) || (exactMatch && term.startDate.isSameOrBefore(date, 'date') && term.endDate.isSameOrAfter(date, 'date')));
  if (!matchingTerm) {
    return null;
  }

  const termNumber = danceSchool.latestEnrolmentYearSettings.termDates.indexOf(matchingTerm) + 1;
  return {
    ...matchingTerm,
    termNumber
  }
}

export const mapSelectedClassTrials = (danceSchool: IDanceSchool, selectedClasses: IDanceClass[], existingSelections: ISelectedClassTrial[]): ISelectedClassTrial[] => {
  return selectedClasses
    .map(x => ({
      classDetails: x,
      classDate: getClassDate(x),
      trialDateOptions: [],
      selectedTrialDate: existingSelections.find(y => y.classDetails === x)?.selectedTrialDate ?? ''
    }))
    .map(x => {
      const trialDateOptions: { date: Dayjs; termNumber: number }[] = [];

      let currentTrialDateOption: Dayjs = x.classDate;
      if (currentTrialDateOption.isBefore(dayjs(), 'date')) {
        currentTrialDateOption = currentTrialDateOption.add(1, 'week');
      }

      for (let i = 0; i < 3; i++) {
        let hasValidTrialDate = false;
        let weeksAdded = 0;
        let dateTermNumber = 1;

        while (!hasValidTrialDate && weeksAdded < 52) {

          for (const term of danceSchool.latestEnrolmentYearSettings.termDates) {
            const isInTerm = (term.startDate.isBefore(currentTrialDateOption) && term.endDate.isAfter(currentTrialDateOption));
            hasValidTrialDate = hasValidTrialDate || isInTerm;

            if (isInTerm) {
              dateTermNumber = danceSchool.latestEnrolmentYearSettings.termDates.indexOf(term) + 1;
            }

            if (hasValidTrialDate) {
              break;
            }
          }

          if (!hasValidTrialDate) {
            currentTrialDateOption = currentTrialDateOption.add(1, 'week');
            weeksAdded++;
          }
        }

        if (hasValidTrialDate) {
          trialDateOptions.push({
            date: currentTrialDateOption,
            termNumber: dateTermNumber
          });
          currentTrialDateOption = currentTrialDateOption.add(1, 'week');
        }
      }

      const currentTerm = getTermForDate(dayjs(), danceSchool);
      const trialDateMenuItemOptions: IOptionsItem[] = [];
      let termIndicator = currentTerm?.termNumber ?? 1;
      for (const option of trialDateOptions) {

        let label = option.date.format('DD MMM YYYY');
        if (option.termNumber !== termIndicator) {  
          label += ` (Term ${option.termNumber})`;
        }

        trialDateMenuItemOptions.push({
          label,
          value: option.date.format(DATE_FORMATS.YearMonthDayIso)
        } as IOptionsItem);

        termIndicator = option.termNumber;
      }

      return {
        ...x,
        trialDateOptions: trialDateMenuItemOptions
      };
    })
    .sort((a, b) => a.classDate.isBefore(b.classDate) ? -1 : a.classDate.isAfter(b.classDate) ? 1 : 0)
}